import { useCallback, useMemo } from "react";
import { Box, Backdrop, Card, CardContent, Dialog, Typography, IconButton } from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import dayjs from "dayjs";
import axios from "../utils/axios";
import IndividualActivityForm from "./IndividualActivityForm";
import { buildStoryPayload } from "./redesign/StoryPrompt";
import FormLeaveConfirmDialog from "./FormLeaveConfirmDialog";
import { useFormCloseGuard } from "../hooks/useFormCloseGuard";
import { setProfile } from "../reducers/profile";
import { inputFieldValidator, descriptionValidator, buildDescriptionFields } from "../utils/activityForm";

const cardPadding = { padding: [0, 2, 4] };

const useStyles = makeStyles(() => ({
  cardContent: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
}));

export default function LogAnActivity({ open, onClose, initialValuesToDuplicate }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const handleLeave = useCallback(() => onClose(), [onClose]);
  const {
    confirmOpen,
    confirmLeave,
    cancelLeave,
    handleBackdropClick,
    requestLeave,
  } = useFormCloseGuard({ onLeave: handleLeave });

  const initialValues = useMemo(
    () => ({
      title: "",
      goal: null,
      activity_type: null,
      what_did_you_do: "",
      what_did_you_learn: "",
      ai_summary: "",
      ai_summary_tagged: false,
      location: null,
      address: "",
      sessions: [{ date: null, hours: "" }],
      skills: [],
      evidence_photo: null,
      evidence_certificate: null,
      reference_contact_name: "",
      reference_contact_email: "",
      eligibility_confirmed: false,
      ...initialValuesToDuplicate,
      // A duplicated log never carries over the previous story.
      story: "",
      story_consent: false,
    }),
    [initialValuesToDuplicate]
  );

  const LogActivitySchema = useCallback(
    () =>
      Yup.object().shape({
        title: Yup.string().min(1, "Too Short!").max(50, "Too Long!").required("Required"),
        goal: Yup.object()
          .required("Please choose a goal")
          .shape({ id: Yup.number().required("Please choose a goal") })
          .nullable(),
        activity_type: Yup.object()
          .shape({ id: Yup.number() })
          .nullable(),
        what_did_you_do: inputFieldValidator(Yup),
        what_did_you_learn: inputFieldValidator(Yup),
        ai_summary: descriptionValidator(Yup),
        location: Yup.object()
          .required("Please choose a region")
          .shape({ id: Yup.number().required("Please choose a region") })
          .nullable(),
        sessions: Yup.array().of(
          Yup.object().shape({
            date: Yup.date().typeError("Please enter a date")
              .max(dayjs(), "Session must be in the past")
              .required("Date is required"),
            hours: Yup.number()
              .required("Hours is required")
              .min(0.5, "Minimum 0.5 hours")
              .max(8, "Maximum 8 hours"),
          })
        ).min(1, "At least one session is required")
         .test("max-12-per-day", "", function (sessions) {
           if (!sessions) return true;
           const hoursPerDate = {};
           sessions.forEach((s) => {
             if (s.date && s.hours) {
               const key = dayjs(s.date).format("YYYY-MM-DD");
               hoursPerDate[key] = (hoursPerDate[key] || 0) + Number(s.hours);
             }
           });
           for (const [date, total] of Object.entries(hoursPerDate)) {
             if (total > 12) {
               return this.createError({ message: `Total hours on ${dayjs(date).format("DD MMM YYYY")} cannot exceed 12 (currently ${total})` });
             }
           }
           return true;
         }),
        skills: Yup.array().of(Yup.object().shape({ id: Yup.number() })),
        eligibility_confirmed: Yup.boolean().oneOf([true], "Please confirm this activity meets SVA's volunteering guidelines"),
      }),
    []
  );

  const handleSubmit = useCallback(
    async (values) => {
      const {
        sessions,
        title,
        skills,
        location,
        goal,
        address,
        activity_type: activityType,
        what_did_you_do,
        what_did_you_learn,
        beneficiary,
        evidence_photo,
        evidence_certificate,
        reference_contact_name,
        reference_contact_email,
        story,
        story_consent,
      } = values;

      const firstDate = sessions[0].date;
      const totalHours = sessions.reduce((sum, s) => sum + Number(s.hours || 0), 0);

      // Set time to 00:00:00 for start datetime
      const startDateTime = dayjs(firstDate)
        .hour(0)
        .minute(0)
        .second(0);

      // Calculate end datetime by adding total hours
      const endDateTime = startDateTime.add(totalHours, "hours");

      const obj = {
        mode: "individual",
        title,
        goal_id: goal?.id,
        location_id: location?.id,
        activity_type_id: activityType?.id,
        what_did_you_do,
        what_did_you_learn,
        ...buildDescriptionFields(values),
        beneficiary_id: beneficiary?.id || null,
        activity_date: dayjs(firstDate).format("YYYY-MM-DD"),
        starts: startDateTime.toISOString(),
        ends: endDateTime.toISOString(),
        skills: (skills || []).map((s) => ({ id: s.id })),
        address,
        sessions: sessions.map((s) => ({
          date: dayjs(s.date).format("YYYY-MM-DD"),
          hours: Number(s.hours),
        })),
        ...buildStoryPayload(story, story_consent),
      };

      // Optional supporting evidence for backlogged activities.
      if (evidence_photo) {
        const upload = await axios.post("uploads", { file: evidence_photo });
        obj.evidence_photo_id = upload.data.model_id;
      }
      if (evidence_certificate) {
        const upload = await axios.post("uploads", { file: evidence_certificate });
        obj.evidence_certificate_id = upload.data.model_id;
      }
      if (reference_contact_name) obj.reference_contact_name = reference_contact_name;
      if (reference_contact_email) obj.reference_contact_email = reference_contact_email;

      await axios.post("activities", obj);

      // Refresh profile to update completed_activity_ids, hours, and awards
      const profileResponse = await axios.get("auth/profile");
      dispatch(setProfile(profileResponse.data.data));

      enqueueSnackbar("Activity logged successfully", { variant: "success" });
      onClose(true);
    },
    [onClose, enqueueSnackbar, dispatch]
  );

  return (
    <>
      <Dialog
        BackdropComponent={styled(Backdrop, {
          name: "MuiModal",
          slot: "Backdrop",
          overridesResolver: (props, styles) => styles.backdrop,
        })({ zIndex: -1 })}
        open={open}
        onClose={handleBackdropClick}
        fullWidth
        maxWidth="md"
        scroll="body"
        transitionDuration={{ appear: 0 }}
      >
        <Card sx={cardPadding}>
          <CardContent className={classes.cardContent}>
            <Box width="100%" display="flex" flexDirection="row" mb={1}>
              <Box flexGrow={1} />
              <Typography sx={{ fontSize: 28, fontWeight: 800 }}>Log an activity</Typography>
              <Box flexGrow={1} justifyContent="flex-end" display="flex">
                <IconButton onClick={requestLeave} aria-label="Close log an activity form">
                  <Close color="primary" />
                </IconButton>
              </Box>
            </Box>
            <IndividualActivityForm initialValues={initialValues} LogActivitySchema={LogActivitySchema} handleSubmit={handleSubmit} showStoryPrompt />
          </CardContent>
        </Card>
      </Dialog>
      <FormLeaveConfirmDialog
        open={confirmOpen}
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
        formTitle="Log an Activity"
      />
    </>
  );
}

LogAnActivity.defaultProps = {
  initialValuesToDuplicate: {},
};

LogAnActivity.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialValuesToDuplicate: PropTypes.shape({}),
};
